/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 **/

import { EmotionJSX } from "@emotion/react/types/jsx-namespace";
import { Row } from "@/frameworks/designStuff/components";
import React, { forwardRef } from "react";
import { S } from "./private";
import { Maybe } from "@/frameworks/libraries/components";

interface TextFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
	value?: string;
	disabled?: boolean;
	success?: boolean;
	error?: boolean;
	help?: string;
	label?: string;
	labelIcon?: React.ReactNode;
	subLabel?: string;
	size?: 32 | 36 | 40 | 44;
	kind?: "input" | "textarea";
	right?: number;
	dropdownContent?: React.ReactNode;
}

interface IconInputProps extends TextFieldProps {
	leftIcon?: EmotionJSX.Element;
	rightIcon?: EmotionJSX.Element;
}

interface TagInputProps extends TextFieldProps {
	tags?: React.ReactNode[];
	maxHeight?: UnitString;
	minHeight?: UnitString;
	verticalAlign?: "center" | "top" | "bottom" | "distribute";
}

const Input = forwardRef(
	(
		{ error = false, success = false, kind = "input", labelIcon, ...props }: TextFieldProps,
		ref: React.ForwardedRef<HTMLInputElement>
	) => {
		return (
			<S.Wrapper gap={4}>
				{(props.label || props.subLabel) && (
					<S.LabelWrapper horizonAlign="distribute">
						<Row verticalAlign="center" gap={2}>
							<S.LabelText>{props.label}</S.LabelText>
							<Maybe test={Boolean(labelIcon)}>{labelIcon}</Maybe>
						</Row>
						<S.LabelText>{props.subLabel}</S.LabelText>
					</S.LabelWrapper>
				)}
				<S.Input tag={kind} error={error} ref={ref} size={props.size || 40} {...props} />
				{props.dropdownContent && <S.DropdownWrapper>{props.dropdownContent}</S.DropdownWrapper>}
				{props.help && (
					<S.HelpText disabled={props.disabled} error={error} success={success}>
						{props.help}
					</S.HelpText>
				)}
			</S.Wrapper>
		);
	}
);

const IconInput = forwardRef(
	(
		{ error = false, success = false, kind = "input", leftIcon, rightIcon, labelIcon, ...props }: IconInputProps,
		ref: React.ForwardedRef<HTMLInputElement>
	) => {
		return (
			<S.Wrapper gap={4}>
				{(props.label || props.subLabel) && (
					<S.LabelWrapper horizonAlign="distribute">
						<Row verticalAlign="center" gap={2}>
							<S.LabelText>{props.label}</S.LabelText>
							<Maybe test={Boolean(labelIcon)}>{labelIcon}</Maybe>
						</Row>
						<S.LabelText>{props.subLabel}</S.LabelText>
					</S.LabelWrapper>
				)}
				<S.IconWrapper>
					{leftIcon && (
						<S.LeftIconWrapper
							verticalAlign="center"
							size={leftIcon?.props.size}
							disabled={props.disabled}
							fill={leftIcon?.props.fill}
						>
							{leftIcon}
						</S.LeftIconWrapper>
					)}
					<S.IconInput
						tag={kind}
						error={error}
						ref={ref}
						size={props.size || 40}
						{...props}
						css={{ paddingLeft: leftIcon && "36px", paddingRight: rightIcon && "40px" }}
					/>
					{rightIcon && (
						<S.RightIconWrapper
							verticalAlign="center"
							disabled={props.disabled}
							fill={rightIcon?.props.fill}
							{...(props.right && { css: { right: `${props.right}px` } })}
						>
							{rightIcon}
						</S.RightIconWrapper>
					)}
				</S.IconWrapper>
				{props.dropdownContent && <S.DropdownWrapper>{props.dropdownContent}</S.DropdownWrapper>}
				{props.help && (
					<S.HelpText disabled={props.disabled} error={error} success={success}>
						{props.help}
					</S.HelpText>
				)}
			</S.Wrapper>
		);
	}
);

const Tag = forwardRef(
	(
		{
			error = false,
			success = false,
			kind = "input",
			maxHeight = "132px",
			minHeight = "76px",
			verticalAlign = "center",
			...props
		}: TagInputProps,
		ref: React.ForwardedRef<HTMLInputElement>
	) => {
		return (
			<S.Wrapper gap={4}>
				{(props.label || props.subLabel) && (
					<S.LabelWrapper horizonAlign="distribute">
						<S.LabelText>{props.label}</S.LabelText>
						<S.LabelText>{props.subLabel}</S.LabelText>
					</S.LabelWrapper>
				)}
				<S.TagWrapper
					error={error}
					gap={4}
					verticalAlign={verticalAlign}
					maxHeight={maxHeight}
					minHeight={minHeight}
				>
					{props.tags && props.tags.map((tag) => tag)}
					<Row tag={kind} ref={ref} css={{ width: "100%" }} {...props} />
				</S.TagWrapper>
				{props.dropdownContent && <S.DropdownWrapper>{props.dropdownContent}</S.DropdownWrapper>}
				{props.help && (
					<S.HelpText disabled={props.disabled} error={error} success={success}>
						{props.help}
					</S.HelpText>
				)}
			</S.Wrapper>
		);
	}
);

interface ITextField extends React.ForwardRefExoticComponent<TextFieldProps & React.RefAttributes<HTMLInputElement>> {
	/**
	 * @param tags TextField 내부에 표시될 태그 배열
	 * ```tsx
	 * const tags = [1, 2, 3];
	 *
	 * return <TextField.Tag tags={tags.map((tag) => <Chip>{tag}</Chip>)}/>
	 * ```
	 * @param maxHeight TextField 최대 높이 (default: 132px)
	 * @param minHeight TextField 최소 높이 (default: 76px)
	 * @param verticalAlign TextField 상하 정렬 (default: center)
	 */
	Tag: typeof Tag;
	/**
	 * @param leftIcon TextField의 왼쪽에 표시될 아이콘
	 * ```tsx
	 * return <TextField.Icon leftIcon={<Icon name="Search24" size={24}/>}/>
	 * ```
	 * @param rightIcon TextField의 오른쪽에 표시될 아이콘
	 *  ```tsx
	 * return <TextField.Icon rightIcon={<Icon name="Close24" size={24}/>}/>
	 * ```
	 */
	Icon: typeof IconInput;
}

/**
 * @param {boolean} error - 에러 표시 여부
 * @param {string} help - 텍스트 필드 하단 헬프 텍스트
 * @param {string} label - 텍스트 필드 좌측 상단 라벨 텍스트
 * @param {string} subLabel - 텍스트 필드 우측 상단 라벨 텍스트
 * @param {number} size - 텍스트 필드 높이값 [32 | 36 | 40: default | 44]
 * @param {string} kind - 텍스트 필드 종류 ["input" | "textarea"]
 * @param {React.ReactNode} dropdownContent - 텍스트 필드 아래에 표시되는 영역
 */
const TextField = {
	...Input,
	Tag,
	Icon: IconInput
} as ITextField;

/**
 * export default function 형식으로 작업시 컴파운드 컴포넌트 형태를 사용할 수 없기 때문에
 * 컴파운드 컴포넌트 형태의 작업을 위해 다른 파일과 export 방식을 다르게 작업했습니다.
 */
export default TextField;
